import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Synchronise les valeurs de filtres avec l'URL (query string).
 * Toutes les valeurs de filtres sont toujours reflétées dans l'URL.
 */
export function useUrlFilters(defaults = {}) {
  const [searchParams, setSearchParams] = useSearchParams()

  const get = useCallback(
    (key) => searchParams.get(key) ?? defaults[key] ?? '',
    [searchParams, defaults],
  )

  const getNumber = useCallback(
    (key, fallback) => {
      const raw = searchParams.get(key)
      const parsed = parseInt(raw ?? '', 10)
      return Number.isNaN(parsed) ? fallback ?? defaults[key] : parsed
    },
    [searchParams, defaults],
  )

  const setParam = useCallback(
    (key, value, { resetPage = true } = {}) => {
      const next = new URLSearchParams(searchParams)
      if (value === '' || value == null || value === 'all') next.delete(key)
      else next.set(key, String(value))
      if (resetPage && key !== 'page') next.delete('page')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const setParams = useCallback(
    (patch, { resetPage = true } = {}) => {
      const next = new URLSearchParams(searchParams)
      Object.entries(patch).forEach(([key, value]) => {
        if (value === '' || value == null || value === 'all') next.delete(key)
        else next.set(key, String(value))
      })
      if (resetPage) next.delete('page')
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  const clear = useCallback(
    (keepKeys = []) => {
      const next = new URLSearchParams()
      keepKeys.forEach((key) => {
        const value = searchParams.get(key)
        if (value) next.set(key, value)
      })
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  return { searchParams, get, getNumber, setParam, setParams, clear }
}
